import { dFix } from '../Settings';
import { Order, OrderAdjustment, OrderItem } from '../types/Order';
import { calculateOrderItemSummary } from './Cart';
import { useOrderItemList, useOrderItemListRows } from './OrderItemList';

interface BasicCheckOutArgs {
	adjustment: OrderAdjustment[];
	orderItem: OrderItem[];
	orderId: string;
	promotionCode?: Order['promotionCode'];
}

const getItemLevelPromotionCodeSavings = (
	adjustment: OrderAdjustment[],
	promotionCode: Order['promotionCode'] = []
) => {
	const promotionIds = promotionCode
		.flatMap(({ associatedPromotion }) => associatedPromotion ?? [])
		.map(({ promotionId }) => promotionId)
		.filter((id): id is string => Boolean(id));
	return adjustment.reduce((acc, { code, displayLevel, amount }) => {
		if (displayLevel === 'Order' || !promotionIds.some((id) => code?.endsWith(`-${id}`))) {
			return acc;
		}
		return acc + dFix(amount || 0) * -1;
	}, 0);
};

export const useBasicCheckOut = ({
	adjustment,
	orderItem,
	orderId,
	promotionCode,
}: BasicCheckOutArgs) => {
	const itemLevelPromotionCodeSavings = getItemLevelPromotionCodeSavings(adjustment, promotionCode);
	const totalCoupons =
		adjustment.reduce((acc, code) => {
			if (code.displayLevel !== 'Order') {
				return acc;
			}
			return acc + dFix(code.amount || 0) * -1;
		}, 0) + itemLevelPromotionCodeSavings;

	const orderItemList = useOrderItemList(orderItem, orderId);
	const orderItemListRows = useOrderItemListRows(orderItemList.data);

	const orderItemListRowsSorted = [...orderItemListRows.data].sort((a, b) => {
		const timeA = new Date(
			a.orderItemListRow.orderItem.lastUpdateDate || a.orderItemListRow.orderItem.createDate || 0
		).getTime();
		const timeB = new Date(
			b.orderItemListRow.orderItem.lastUpdateDate || b.orderItemListRow.orderItem.createDate || 0
		).getTime();
		return timeB - timeA;
	});

	const { totalDiscount, totalPriceWithoutOffer } = orderItemListRowsSorted.reduce(
		(acc, row) => {
			const { discount, priceWithoutOffer } = calculateOrderItemSummary({
				offerPrice: row.details.prices.offer || 0,
				displayPrice: row.details.prices.list || 0,
				totalAdjustment: row?.orderItemListRow.totalAdjustment.totalAdjustment,
				orderItemPrice: row?.orderItemListRow.price.orderItemPrice,
			});
			return {
				totalDiscount: acc.totalDiscount + discount,
				totalPriceWithoutOffer: acc.totalPriceWithoutOffer + priceWithoutOffer,
			};
		},
		{ totalDiscount: 0, totalPriceWithoutOffer: 0 }
	);

	return {
		totalCoupons,
		totalDiscount,
		totalPriceWithoutOffer,
		orderItemList,
		orderItemListRows,
		orderItemListRowsSorted,
		loadingOrderItemListRows: orderItemListRows.loading,
	};
};
